'use client';

import Link from 'next/link';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CancelIcon from '@mui/icons-material/Cancel';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import { Box, Card, Typography, useTheme } from '@mui/material';

import { Avatar, ComponentErrorBoundary } from '@/components';
import type { EventResponse } from '@/types';
import {
  areAllMarketsResolved,
  findResolvedOutcome,
  getEventType,
  normalizeMarketQuestion,
} from '@/utils';
import { formatProbability, formatTimeRemaining, formatVolume } from '@/utils/formatters';

import { ROUTES } from '@/constants';

interface SearchMarketCardProps {
  event: EventResponse;
}

const MetadataItem = ({
  icon: Icon,
  text,
  color,
}: {
  icon?: any;
  text: string;
  color?: string;
}) => (
  <Typography
    variant="caption"
    sx={{
      color: 'text.secondary',
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
      fontWeight: 500,
    }}
  >
    {Icon && <Icon sx={{ fontSize: '0.875rem', color: color || 'inherit' }} />}
    {text}
  </Typography>
);

export function SearchMarketCard({ event }: SearchMarketCardProps) {
  const theme = useTheme();

  if (!event) return null;
  const market = event.markets?.[0];
  if (!market) return null;

  let outcomes = [];
  try {
    outcomes =
      typeof market.outcomes === 'string' ? JSON.parse(market.outcomes) : market.outcomes || [];
  } catch (e) {
    // Ignore parse errors
  }

  const firstOutcome = outcomes[0];
  const probability = firstOutcome?.price ? parseFloat(firstOutcome.price.toString()) : 0;
  const probabilityText = formatProbability(probability);
  const outcomeValue = normalizeMarketQuestion(market);
  // For a single-question market, normalizeMarketQuestion just returns the
  // market's own question — identical to the event title already shown above
  // it. Only worth a second line when it actually differs (grouped events,
  // where this names the specific sub-market the shown probability is for).
  const showOutcomeLabel = !!outcomeValue && outcomeValue !== event.title;
  const resolvedStatus = findResolvedOutcome(event, market);
  const volume = event?.volume || 0;
  const volumeToday = event.volume24hr;
  const liquidity = event?.liquidity || 0;
  const commentCount = event?.commentCount || 0;
  const timeRemainingText = formatTimeRemaining(event?.endDate);

  const moreCount = (event.markets?.length || 0) - 1;
  const isResolved = areAllMarketsResolved(event);

  const eventType = getEventType(event);
  const href =
    eventType === 'SPORTS_CARD'
      ? ROUTES.GAME_DETAIL(
          (event.league || event.tags?.[0]?.slug || 'live').toLowerCase(),
          event.id,
        )
      : ROUTES.MARKET_DETAILS(event.slug || event.id || '');

  return (
    <ComponentErrorBoundary componentName="SearchMarketCard">
      <Card
        component={Link}
        href={href}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: { xs: 'flex-start', sm: 'center' },
          p: { xs: 2 },
          textDecoration: 'none',
          borderRadius: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: 'none',
          bgcolor: 'transparent',
          opacity: isResolved ? 0.7 : 1,
          filter: isResolved ? 'grayscale(0.3)' : 'none',
          '&:hover': {
            bgcolor: 'action.hover',
            opacity: 1,
            filter: 'none',
          },
          transition: 'all 0.2s ease',
        }}
      >
        {/* Market Icon */}
        <Avatar
          src={event.image || ''}
          alt={event.title || 'Market'}
          size={52}
          sx={{ mr: { xs: 1.5, sm: 2.5 }, mt: { xs: 0.5, sm: 0 }, flexShrink: 0 }}
        />

        {/* Info Content */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {/* Title and Odds Row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            {/* Market Title */}
            <Typography
              variant="body1"
              sx={{
                flex: 1,
                fontWeight: 700,
                color: 'text.primary',
                lineHeight: 1.3,
                fontSize: { xs: '0.9rem', sm: '1.05rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                mt: { xs: 0, sm: 0.5 },
              }}
            >
              {event.title || 'Untitled Market'}
            </Typography>

            {/* Odds / Probability or Resolved Status */}
            <Box
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexShrink: 0,
              }}
            >
              <Box sx={{ textAlign: 'right', mr: { xs: 0, sm: 1 }, minWidth: 0 }}>
                {resolvedStatus && areAllMarketsResolved(event) ? (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        lineHeight: 1,
                        fontSize: { xs: '0.95rem', sm: '1.1rem' },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                      }}
                    >
                      {resolvedStatus.displayName || resolvedStatus.name}
                      {resolvedStatus.name?.toLowerCase() === 'no' ? (
                        <CancelIcon sx={{ color: theme.palette.error.main, fontSize: '1.1rem' }} />
                      ) : (
                        <CheckCircleIcon color="success" sx={{ fontSize: '1.1rem' }} />
                      )}
                    </Typography>
                    {moreCount > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          mt: 0.5,
                          display: 'block',
                        }}
                      >
                        + {moreCount} more
                      </Typography>
                    )}
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: 'text.primary',
                        lineHeight: 1,
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      {`${probabilityText}%`}
                    </Typography>
                    {showOutcomeLabel && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 600,
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          mt: 0.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-word',
                        }}
                      >
                        {outcomeValue}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              <ChevronRightIcon
                sx={{
                  color: 'text.disabled',
                  fontSize: { xs: '1.25rem', sm: '1.5rem' },
                  display: { xs: 'none', sm: 'block' },
                }}
              />
            </Box>
          </Box>

          {/* Metadata Tags */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 1.5, sm: 2.5 },
              mt: 0.5,
            }}
          >
            {volume > 0 && <MetadataItem text={`${formatVolume(volume)} Vol.`} />}

            {volumeToday > 0 && (
              <MetadataItem
                icon={LocalFireDepartmentIcon}
                text={`${formatVolume(volumeToday)} today`}
                color={theme.palette.error.main}
              />
            )}

            {liquidity > 0 && (
              <MetadataItem
                icon={WaterDropIcon}
                text={`${formatVolume(liquidity)} Liq.`}
                color={theme.palette.info.main}
              />
            )}

            {commentCount > 0 && (
              <MetadataItem icon={ChatBubbleOutlineIcon} text={commentCount.toString()} />
            )}

            {timeRemainingText && <MetadataItem icon={AccessTimeIcon} text={timeRemainingText} />}
          </Box>
        </Box>
      </Card>
    </ComponentErrorBoundary>
  );
}
